import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const DEFAULT_DAILY_LIMIT = 100000;

/**
 * Hook to check and enforce per-user daily download row limits.
 *
 * Returns:
 * - todayUsed: rows already downloaded today
 * - dailyLimit: the user's configured limit
 * - remaining: rows still available today
 * - checkAndLog: call before downloading — returns { allowed, remaining } and
 *   inserts a download_logs row if allowed
 * - refreshUsage: re-fetch today's usage from DB
 */
export function useDownloadLimit() {
  const { profile } = useAuth();
  const [todayUsed, setTodayUsed] = useState(0);
  const [loading, setLoading] = useState(true);

  const dailyLimit = profile?.daily_download_limit ?? DEFAULT_DAILY_LIMIT;

  const fetchTodayUsage = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);

    // Get start of today in UTC
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from("download_logs" as any)
      .select("row_count")
      .eq("profile_id", profile.id)
      .gte("downloaded_at", todayStart.toISOString());

    if (!error && data) {
      const total = (data as any[]).reduce(
        (sum: number, row: any) => sum + (row.row_count || 0),
        0
      );
      setTodayUsed(total);
    }
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    fetchTodayUsage();
  }, [fetchTodayUsage]);

  const remaining = Math.max(0, dailyLimit - todayUsed);

  /**
   * Check whether `rowCount` rows can be downloaded.
   * If allowed, inserts a log row and updates local state.
   * Returns { allowed, remaining }.
   */
  const checkAndLog = useCallback(
    async (rowCount: number): Promise<{ allowed: boolean; remaining: number }> => {
      if (!profile?.id) return { allowed: false, remaining: 0 };

      // Re-fetch fresh usage to avoid stale state
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const { data } = await supabase
        .from("download_logs" as any)
        .select("row_count")
        .eq("profile_id", profile.id)
        .gte("downloaded_at", todayStart.toISOString());

      const freshUsed = (data as any[] || []).reduce(
        (sum: number, row: any) => sum + (row.row_count || 0),
        0
      );
      const freshRemaining = Math.max(0, dailyLimit - freshUsed);

      if (rowCount > freshRemaining) {
        setTodayUsed(freshUsed);
        return { allowed: false, remaining: freshRemaining };
      }

      // Log the download
      await supabase.from("download_logs" as any).insert({
        profile_id: profile.id,
        row_count: rowCount,
      });

      const newUsed = freshUsed + rowCount;
      setTodayUsed(newUsed);
      return { allowed: true, remaining: Math.max(0, dailyLimit - newUsed) };
    },
    [profile?.id, dailyLimit]
  );

  return {
    todayUsed,
    dailyLimit,
    remaining,
    loading,
    checkAndLog,
    refreshUsage: fetchTodayUsage,
  };
}
